/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';
import type { RoutePattern, RouterState } from 'react-router';
import { Link as ReactRouterLink } from 'react-router';

import type { LocationDescriptorObject } from 'history';

import { Link, breakpoints } from '@coursera/cds-core';
import type { LinkProps } from '@coursera/cds-core';

import VideoThumbnail from 'bundles/clips/components/VideoThumbnail';
import { END_OF_VIDEO_LINK_TRACKING_NAME } from 'bundles/clips/components/short-form-content/constants';
import VideoThumbnailQuery from 'bundles/clips/queries/VideoThumbnailQuery';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import type { OnDemandLectureVideosV1 } from 'bundles/naptimejs/resources/__generated__/OnDemandLectureVideosV1';
import type { OnDemandVideosV1 } from 'bundles/naptimejs/resources/__generated__/OnDemandVideosV1';

type PatchedLocationDescriptor = LocationDescriptorObject & {
  name?: string;
  params?: RouterState['params'];
  search?: string;
};

type PropsFromCaller = {
  itemId: string;
  name: string;
  courseId?: string;
  to: RoutePattern | PatchedLocationDescriptor | ((...args: any) => LocationDescriptorObject);
};

type VideoThumbnailQueryResponse = {
  OnDemandLectureVideosV1: {
    get: {
      elements: OnDemandLectureVideosV1[];
      linked: {
        onDemandVideosV1: OnDemandVideosV1[];
      };
    };
  };
};

type VideoThumbnailQueryVariables = {
  id: string;
};

type PropsFromGraphql = {
  thumbnailSrcUrl?: string;
};

export type Props = PropsFromCaller & PropsFromGraphql;

const styles = {
  thumbnailLink: () => css`
    max-width: 200px;
  `,
  startText: () => css`
    padding: 0 var(--cds-spacing-100);
    ${breakpoints.down('xs')} {
      display: none;
    }
  `,
};

const TrackedLink = withSingleTracked({ type: 'BUTTON' })<LinkProps<ReactRouterLink>>(
  // TODO: Pattern we commonly use that we don't have a standard to type properly yet
  // so disabling errors
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  Link
);

export const VideoThumbnailLink: React.FC<Props> = ({ name, thumbnailSrcUrl, to }) => {
  return (
    <div css={styles.thumbnailLink} data-testid="video-thumbnail-link">
      <TrackedLink
        {...Link.defaultProps}
        variant="quiet"
        typographyVariant="bodySecondary"
        component={ReactRouterLink}
        to={to}
        trackingName={END_OF_VIDEO_LINK_TRACKING_NAME}
      >
        <VideoThumbnail src={thumbnailSrcUrl} alt={name} />
      </TrackedLink>
    </div>
  );
};

export default graphql<PropsFromCaller, VideoThumbnailQueryResponse, VideoThumbnailQueryVariables, PropsFromGraphql>(
  VideoThumbnailQuery,
  {
    skip: ({ courseId }) => !courseId,
    options: ({ courseId, itemId }) => ({ variables: { id: courseId + '~' + itemId } }),
    props: ({ data }) => {
      const thumbnailSrcUrl =
        data?.OnDemandLectureVideosV1?.get?.linked?.onDemandVideosV1[0]?.sources?.byResolution['360P']?.previewImageUrl;
      return {
        thumbnailSrcUrl,
      };
    },
  }
)(VideoThumbnailLink);
